import { LayoutChangeEvent, Platform, StyleProp, ViewStyle } from 'react-native';
import { useElementPosition } from '../../../../hooks/useElementPosition';

export type UseSuggestionPositionArgs = {
    shouldShowSuggestions: boolean;
};

export type UseSuggestionPositionReturnValue = {
    inputRef: any;
    onInputLayout: (e: LayoutChangeEvent) => void;
    suggestionPositionStyling: StyleProp<ViewStyle>;
    suggestionAboveOrBelow: AboveOrBelow;
};

export type AboveOrBelow = 'above' | 'below';

type SuggestionPlacement = {
    aboveOrBelow: AboveOrBelow;
    maxHeight: number;
};

const HEIGHT_BUFFER = 75;
const MAX_HEIGHT = 400;
const MIN_HEIGHT = 200;

export const useSuggestionPosition = ({
    shouldShowSuggestions,
}: UseSuggestionPositionArgs): UseSuggestionPositionReturnValue => {
    const inputPosition = useElementPosition();

    const getSuggestionPlacement = (): SuggestionPlacement | null => {
        if (!inputPosition.position) return null;

        const isOnBottomHalfOfScreen = !inputPosition.position.isOnTopHalfOfScreen;

        return isOnBottomHalfOfScreen
            ? {
                  aboveOrBelow: 'above',
                  maxHeight: Math.max(inputPosition.position.getMaxVisibleHeightFromTop() - HEIGHT_BUFFER, MIN_HEIGHT),
              }
            : {
                  aboveOrBelow: 'below',
                  maxHeight: Math.min(
                      inputPosition.position.getMaxVisibleHeightFromBottom() - HEIGHT_BUFFER,
                      MAX_HEIGHT,
                  ),
              };
    };

    const suggestionPlacement = getSuggestionPlacement();

    let suggestionPositionStyling: StyleProp<ViewStyle> = {};

    if (inputPosition.position && suggestionPlacement && shouldShowSuggestions && suggestionPlacement) {
        suggestionPositionStyling = {
            width: inputPosition?.position?.width || 0,
            left: inputPosition.position?.left || 0,
            top:
                suggestionPlacement.aboveOrBelow === 'below'
                    ? inputPosition.position.getTopAlignFromBottomWithPadding(8)
                    : undefined,
            // iOS does not seem to give an accurate keyboard height.  It seems like it's missing the toolbar height.  Fudge it a bit.  I'm not convinced we shouldn't do a different input UX for mobile
            bottom:
                suggestionPlacement.aboveOrBelow === 'above'
                    ? inputPosition.position.getBottomAlignFromTopWithPadding(Platform.OS === 'ios' ? 44 : 8)
                    : undefined,
            maxHeight: suggestionPlacement.maxHeight,
            borderTopLeftRadius: suggestionPlacement.aboveOrBelow === 'below' ? 0 : undefined,
            borderTopRightRadius: suggestionPlacement.aboveOrBelow === 'below' ? 0 : undefined,
            borderBottomLeftRadius: suggestionPlacement.aboveOrBelow === 'above' ? 0 : undefined,
            borderBottomRightRadius: suggestionPlacement.aboveOrBelow === 'above' ? 0 : undefined,
        };
    }

    return {
        inputRef: inputPosition.ref,
        onInputLayout: inputPosition.onLayout,
        suggestionPositionStyling,
        suggestionAboveOrBelow: suggestionPlacement?.aboveOrBelow || 'below',
    };
};
